<template>
  <div class="pt-2 pb-6 md:py-6">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
      <div class="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 pb-4">
        <h1 class="md:text-3xl text-xl font-medium text-gray-900">{{ $t('community.your_users_title') }}</h1>
        <div class="flex flex-wrap gap-3">
          <router-link v-if="massiveEnroll" :to="{ name: 'community-massive' }"
                       class="inline-flex items-center shadow-sm px-4 py-1.5 transition ease-in border border-gray-300 text-sm leading-5 font-medium rounded-full text-gray-700 bg-white hover:bg-brainspro-400 hover:text-white active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-bpblue">
            {{ $t('community.massive_increase') }}
          </router-link>
          <button v-else type="button" disabled :title="$t('community.massive_plan_only')"
                  class="inline-flex items-center px-4 py-1.5 border border-gray-200 text-sm leading-5 font-medium rounded-full text-gray-500 bg-gray-50 cursor-not-allowed">
            {{ $t('community.massive_increase') }}
          </button>
          <button type="button" v-on:click="navigationMenuStore.toggleSlideOverUserCreation"
                  class="inline-flex items-center shadow-sm px-4 py-1.5 transition ease-in border border-gray-300 text-sm leading-5 font-medium rounded-full text-gray-700 bg-white hover:bg-brainspro-400 hover:text-white active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-bpblue">
            <svg class="-ml-1.5 mr-1 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fill-rule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clip-rule="evenodd"/>
            </svg>
            <span>{{ $t('community.new_user') }}</span>
          </button>
        </div>
      </div>

      <!-- Búsqueda, filtro por tipo y exportación -->
      <div class="mt-5 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
        <div class="w-full sm:max-w-xs">
          <label for="user-search" class="sr-only">{{ $t('community.search_label') }}</label>
          <div class="relative rounded-md shadow-sm">
            <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fill-rule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clip-rule="evenodd"/>
              </svg>
            </div>
            <input id="user-search" type="search" :value="search" v-on:input="onSearchInput($event.target.value)"
                   class="focus:ring-bpblue focus:border-bpblue block w-full pl-10 sm:text-sm border-gray-300 rounded-md placeholder-gray-500"
                   :placeholder="$t('community.search_placeholder')"/>
          </div>
        </div>

        <div class="inline-flex rounded-md shadow-sm" role="group" :aria-label="$t('community.filter_label')">
          <button v-for="(option, index) in typeOptions" :key="option.value" type="button"
                  v-on:click="changeUserType(option.value)" :aria-pressed="type === option.value ? 'true' : 'false'"
                  :class="[
                    type === option.value ? 'bg-brainspro-100 text-brainspro-800' : 'bg-white text-gray-700 hover:bg-gray-50',
                    index === 0 ? 'rounded-l-md' : '-ml-px',
                    index === typeOptions.length - 1 ? 'rounded-r-md' : ''
                  ]"
                  class="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium focus:z-10 focus:outline-none focus:ring-2 focus:ring-bpblue">
            {{ $t(option.label) }}
          </button>
        </div>

        <button type="button" v-on:click="exportUsers" :disabled="exporting"
                :title="$t('community.export_users')" :aria-label="$t('community.export_users')"
                class="inline-flex w-max items-center px-3 py-2 transition ease-in border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-bpblue hover:text-white disabled:opacity-50 active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-bpblue">
          <svg v-if="!exporting" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
          </svg>
          <font-awesome-icon v-else class="h-5 w-5 animate-spin" icon="spinner"/>
        </button>
      </div>

      <div ref="listTop" class="py-4" :aria-busy="loading ? 'true' : 'false'">
        <!-- Carga: esqueleto con la forma de las filas -->
        <ul v-if="loading" class="bg-white shadow sm:rounded-lg divide-y divide-gray-100" aria-hidden="true">
          <li v-for="row in perPage" :key="row" class="flex items-center gap-4 px-6 py-4">
            <div class="h-12 w-12 flex-shrink-0 rounded-full bg-gray-200 animate-pulse motion-reduce:animate-none"></div>
            <div class="flex-1 space-y-2">
              <div class="h-4 w-1/3 max-w-[14rem] rounded bg-gray-200 animate-pulse motion-reduce:animate-none"></div>
              <div class="h-3 w-1/2 max-w-[18rem] rounded bg-gray-100 animate-pulse motion-reduce:animate-none"></div>
            </div>
            <div class="hidden md:block h-3 w-24 rounded bg-gray-100 animate-pulse motion-reduce:animate-none"></div>
          </li>
        </ul>
        <p v-if="loading" class="sr-only" role="status">{{ $t('community.loading_users') }}</p>

        <div v-else-if="loadError" class="rounded-lg border border-red-200 bg-red-50 px-6 py-8 text-center" role="alert">
          <p class="text-sm font-medium text-red-800">{{ $t('community.load_error') }}</p>
          <button type="button" v-on:click="fetchUsers"
                  class="mt-4 inline-flex items-center px-4 py-2 rounded-md border border-red-300 bg-white text-sm font-medium text-red-700 hover:bg-red-50 active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500">
            {{ $t('community.retry') }}
          </button>
        </div>

        <!-- Sin resultados con búsqueda o filtro -->
        <div v-else-if="!users.length && (search || type !== 'all')" class="rounded-lg bg-white shadow px-6 py-12 text-center">
          <p class="text-sm font-medium text-gray-900">
            {{ search ? $t('community.no_results_title', { search }) : $t('community.no_results_filter_title') }}
          </p>
          <p class="mt-1 text-sm text-gray-600">{{ $t('community.no_results_text') }}</p>
          <button type="button" v-on:click="clearFilters"
                  class="mt-4 inline-flex items-center px-4 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-bpblue">
            {{ $t('community.clear_filters') }}
          </button>
        </div>

        <div v-else-if="!users.length" class="rounded-lg bg-white shadow px-6 py-12 text-center">
          <p class="text-sm font-medium text-gray-900">{{ $t('community.empty_title') }}</p>
          <p class="mt-1 text-sm text-gray-600">{{ $t('community.empty_text') }}</p>
          <button type="button" v-on:click="navigationMenuStore.toggleSlideOverUserCreation"
                  class="mt-4 inline-flex items-center px-4 py-2 rounded-md border border-transparent bg-bpblue text-sm font-medium text-white shadow-sm hover:bg-blue-500 active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-bpblue">
            {{ $t('community.new_user') }}
          </button>
        </div>

        <template v-else>
          <div class="hidden md:block shadow overflow-x-auto sm:rounded-lg border-b border-gray-200">
            <table class="min-w-full">
              <thead>
                <tr>
                  <th scope="col" class="px-6 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-600 uppercase tracking-wider">
                    {{ $t('general.name') }}
                  </th>
                  <th scope="col" class="px-6 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-600 uppercase tracking-wider">
                    {{ $t('general.phone') }}
                  </th>
                  <th scope="col" class="px-6 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-600 uppercase tracking-wider">
                    {{ $t('community.last_login') }}
                  </th>
                  <th scope="col" class="px-6 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-600 uppercase tracking-wider">
                    {{ $t('general.date_creation') }}
                  </th>
                  <th scope="col" class="px-6 py-3 border-b border-gray-200 bg-gray-50">
                    <span class="sr-only">{{ $t('community.view_profile') }}</span>
                  </th>
                </tr>
              </thead>
              <tbody class="bg-white">
                <tr v-for="user in users" :key="user.uuid" v-on:click="goToUserProfile(user.uuid)"
                    class="hover:bg-gray-50 transition-colors ease-in cursor-pointer">
                  <td class="px-6 py-4 border-b border-gray-200">
                    <div class="flex items-center gap-4">
                      <img v-if="user.avatar" class="h-10 w-10 flex-shrink-0 rounded-full object-cover shadow" :src="user.avatar" alt="" loading="lazy">
                      <div v-else class="h-10 w-10 flex-shrink-0 overflow-hidden bg-brainspro-400 rounded-full shadow">
                        <img src="/images/avatar-transparent.png" alt="">
                      </div>
                      <div class="min-w-0">
                        <div class="flex flex-wrap items-center gap-2">
                          <span class="text-sm leading-5 font-medium text-gray-900">{{ fullName(user) }}</span>
                          <span v-if="user.role" class="text-xs leading-5 text-brainspro-800 rounded bg-brainspro-100 py-0.5 px-2">{{ user.role }}</span>
                        </div>
                        <div class="text-sm leading-5 text-gray-600 truncate">{{ user.email }}</div>
                      </div>
                    </div>
                  </td>
                  <td class="px-6 py-4 border-b border-gray-200 text-sm leading-5 text-gray-600 whitespace-nowrap">{{ user.phone }}</td>
                  <td class="px-6 py-4 border-b border-gray-200 text-sm leading-5 text-gray-600 whitespace-nowrap tabular-nums">
                    {{ user.last_login ? $moment(user.last_login).format('DD/MM/YYYY H:mm') : '' }}
                  </td>
                  <td class="px-6 py-4 border-b border-gray-200 text-sm leading-5 text-gray-600 whitespace-nowrap tabular-nums">
                    {{ $moment(user.created_at).format('DD/MM/YYYY H:mm') }}
                  </td>
                  <td class="px-6 py-4 text-right border-b border-gray-200">
                    <router-link :to="{ name: 'user-profile', params: { id: user.uuid } }" v-on:click.stop
                                 :title="$t('community.view_profile')" :aria-label="$t('community.view_profile_of', { name: fullName(user) })"
                                 class="inline-flex p-1 h-7 w-7 text-gray-500 rounded-full hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-bpblue">
                      <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd"/>
                      </svg>
                    </router-link>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <mobile-user-list-component class="block md:hidden" :users="users"></mobile-user-list-component>
        </template>

        <PaginationControls
          v-if="!loadError"
          class="mt-6"
          :current-page="meta.current_page"
          :last-page="meta.last_page"
          :total="meta.total"
          :from="meta.from ?? 0"
          :to="meta.to ?? 0"
          :loading="loading"
          @change="goToPage"
        ></PaginationControls>
      </div>
    </div>
  </div>
</template>

<script>
import userService from "@/services/user/UserService";
import PaginationControls from "@/components/navigation/PaginationControls";
import MobileUserListComponent from "@/views/community/Mobile/UserListComponent";
import NotificationService from "@/services/notification/NotificationService";
import {useAuthUserStore} from "@/stores/auth";
import {useAcademiesStore} from "@/stores/academies";
import {useNavigationMenuStore} from "@/stores/navigationMenu";

const PER_PAGE = 10;
const SEARCH_DEBOUNCE_MS = 300;
const USER_TYPES = ['all', 'students', 'teachers'];

const emptyMeta = () => ({ current_page: 1, last_page: 1, total: 0, from: 0, to: 0 });

// Página en la ruta (/users/2, como enlaza el menú); búsqueda y tipo en la query.
const pageFromRoute = (route) => Math.max(1, parseInt(route.params.page, 10) || 1);
const typeFromRoute = (route) => (USER_TYPES.includes(route.query.type) ? route.query.type : 'all');

export default {
  name: "UsersListComponent",
  components: {PaginationControls, MobileUserListComponent},
  setup() {
    return {
      authUserStore: useAuthUserStore(),
      academiesStore: useAcademiesStore(),
      navigationMenuStore: useNavigationMenuStore(),
    }
  },
  data() {
    return {
      loading: true,
      loadError: false,
      exporting: false,
      users: [],
      meta: emptyMeta(),
      perPage: PER_PAGE,
      search: this.$route.query.search ?? '',
      type: typeFromRoute(this.$route),
      typeOptions: [
        { value: 'all', label: 'community.all' },
        { value: 'students', label: 'general.students' },
        { value: 'teachers', label: 'general.teachers' },
      ],
      searchTimer: null,
      // Evita que una respuesta lenta pise la de una búsqueda o página posterior.
      requestId: 0,
    }
  },
  computed: {
    massiveEnroll() {
      const features = this.authUserStore.user?.academy?.saas_subscriptions?.[0]?.saas_plan?.saas_features ?? [];
      return features.some((feature) => feature.key === 'massive-enrol');
    },
  },
  watch: {
    'academiesStore.academy.uuid'(uuid) {
      if (uuid) {
        this.fetchUsers();
      }
    },
    $route(route, previous) {
      if (route.name !== 'users') {
        return;
      }
      if (route.params.page !== previous.params.page
          || route.query.search !== previous.query.search
          || route.query.type !== previous.query.type) {
        this.search = route.query.search ?? '';
        this.type = typeFromRoute(route);
        this.fetchUsers();
      }
    },
  },
  mounted() {
    if (this.academiesStore.academy.uuid) {
      this.fetchUsers();
    }
  },
  beforeUnmount() {
    clearTimeout(this.searchTimer);
  },
  methods: {
    fetchUsers() {
      const requestId = ++this.requestId;
      this.loading = true;
      this.loadError = false;

      userService.paginate({
        academyUuid: this.academiesStore.academy.uuid,
        search: this.$route.query.search ?? '',
        type: typeFromRoute(this.$route),
        page: pageFromRoute(this.$route),
        perPage: this.perPage,
      })
          .then(({ data }) => {
            if (requestId !== this.requestId) {
              return;
            }
            this.users = data.data ?? [];
            this.meta = { ...emptyMeta(), ...data.meta };

            // Si la página pedida ya no existe (p. ej. /users/40 con pocos resultados), se salta a la última.
            if (!this.users.length && this.meta.current_page > this.meta.last_page && this.meta.last_page >= 1) {
              this.navigate({ page: this.meta.last_page }, true);
            }
          })
          .catch(() => {
            if (requestId === this.requestId) {
              this.loadError = true;
            }
          })
          .finally(() => {
            if (requestId === this.requestId) {
              this.loading = false;
            }
          });
    },
    navigate({ page = pageFromRoute(this.$route), ...queryChanges }, replace = false) {
      const query = { ...this.$route.query, ...queryChanges };
      Object.keys(query).forEach((key) => {
        if (query[key] === '' || query[key] === null || (key === 'type' && query[key] === 'all')) {
          delete query[key];
        }
      });
      return this.$router[replace ? 'replace' : 'push']({ name: 'users', params: { page }, query });
    },
    goToPage(page) {
      this.navigate({ page });
      this.$refs.listTop?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    },
    onSearchInput(value) {
      this.search = value;
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        // Una búsqueda nueva siempre empieza en la página 1.
        this.navigate({ page: 1, search: this.search.trim() }, true);
      }, SEARCH_DEBOUNCE_MS);
    },
    changeUserType(type) {
      if (type === this.type) {
        return;
      }
      this.type = type;
      this.navigate({ page: 1, type });
    },
    clearFilters() {
      clearTimeout(this.searchTimer);
      this.search = '';
      this.type = 'all';
      this.navigate({ page: 1, search: '', type: 'all' }, true);
    },
    fullName(user) {
      return [user.firstname, user.lastname].filter(Boolean).join(' ');
    },
    goToUserProfile(userUuid) {
      this.$router.push({name: 'user-profile', params: {id: userUuid}});
    },
    exportUsers() {
      this.exporting = true;
      userService.download(this.academiesStore.academy.uuid)
          .then((response) => {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'community.csv');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
          })
          .catch((error) => {
            NotificationService.showApiError(error, this.$t('community.export_error'));
          })
          .finally(() => {
            this.exporting = false;
          });
    },
  }
}
</script>
